import { formatDate } from "@angular/common";
import { Component, DestroyRef, inject, Input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { CommonModalFormComponent } from "@shared/components/commons/common-modal-form/common-modal-form.component";
import { AutocompleteComponent } from "@shared/controls/autocomplete/autocomplete.component";
import { InputComponent } from "@shared/controls/input/input.component";
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { downloadBase64File } from '@shared/utils/download-base64-file';
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import { ToastrService } from "ngx-toastr";
import { Company } from "@models/companies/company";
import { SelectOption } from "@models/ui-models/select-options";
import { PayslipReason, PayslipReasonLabels, payslipReasonOptions } from "./payslip-reasons.contant";

interface IPayslipFormControls {
  reason: FormControl<SelectOption<string> | null>;
  paidAmount: FormControl<string | null>;
}

type IPayslipFormGroup = FormGroup<IPayslipFormControls>;
type IPayslipFormArray = FormArray<IPayslipFormGroup>;

@Component({
  selector: 'app-generate-payslip',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModalFormComponent,
    InputComponent,
    AutocompleteComponent
  ],
  providers: [],
  templateUrl: 'generate-payslip-modal.component.html',
  styleUrl: 'generate-payslip-modal.component.scss'
})
export class GeneratePayslipModalComponent implements OnInit {
  activeModal = inject(NgbActiveModal);
  destroyRef = inject(DestroyRef);
  companiesApiService = inject(CompaniesApiService);
  toastr = inject(ToastrService);

  @Input() companyProfile: Company | undefined;


  isLoading = signal(false);
  reasonOptionsList: SelectOption<string>[][] = [];
  formArray: IPayslipFormArray = new FormArray<IPayslipFormGroup>([this.definePayslipFormGroup()]);

  ngOnInit(): void {
    const existingDebtOption = payslipReasonOptions.find((option) => option.label == PayslipReasonLabels[PayslipReason.ExistingDebt]);

    if (existingDebtOption) {
      existingDebtOption.value = this.companyProfile?.companyDebt?.amountOwing?.toString() ?? '';
    }

    const firstPayslipFormGroup = this.formArray.controls.at(0);

    if (firstPayslipFormGroup) {
      firstPayslipFormGroup.controls.reason.setValue(existingDebtOption ?? null);
      firstPayslipFormGroup.controls.paidAmount.setValue(existingDebtOption?.value ?? null);
    }

    this.updatedReasonOptionsList();
  }

  listenReasonChanges(form: IPayslipFormGroup): void {
    form.controls.reason.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reason: SelectOption<string> | null) => {
        if (reason && !isNaN(parseFloat(reason.value))) {
          form.controls.paidAmount.patchValue(reason.value);
        }
      });

    form.controls.reason.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updatedReasonOptionsList();
      });
  }

  listenPaidAmountChanges(form: IPayslipFormGroup): void {
    // Special Required validator for paid amount control
    form.controls.paidAmount.addValidators((control: AbstractControl) => {
      const controlValue = control.value as string | null ?? '';
      const includeOnlyDollarSign = controlValue.trim() == '$';

      return includeOnlyDollarSign
        ? { required: true }
        : null;
    });

    form.controls.paidAmount.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const paidAmount = value ?? '';

        if (!paidAmount.includes('$')) {
          const changedPaidAmount = '$' + paidAmount;
          form.controls.paidAmount.patchValue(changedPaidAmount, { emitEvent: false });
        }

        const invalidPattern = /^\$\D.*$/;

        if (invalidPattern.test(paidAmount)) {
          form.controls.paidAmount.patchValue('$', { emitEvent: false });
        }
      });
  }

  downloadPayslip(): void {
    const payload = this.formArray.controls.map((formGroup) => {
      const description = (formGroup.controls.reason.value as SelectOption)?.label;
      const amount = ((formGroup.controls.paidAmount.value ?? '') as string)?.replace('$', '');

      return { description, amount };
    });

    this.setIsLoading();

    this.companiesApiService.generatePayslip(this.companyProfile!.entityId, payload)
      .subscribe({
        next: (file) => {
          downloadBase64File(file, ('Payslip ' + formatDate(new Date(), 'dd-MM-yyyy', 'en-US') + '.pdf'));
          this.activeModal.close();
        },
        error: (error) => {
          console.warn('[generatePayslip] error: ', error);
          this.toastr.error('Error happened while downloading payslip', 'Error');
          this.setIsLoading(false);
        }
      });
  }

  close(): void {
    this.activeModal.close();
  }

  setIsLoading(isLoading = true): void {
    this.isLoading.set(isLoading);
    setControlDisabled(this.formArray, isLoading);
  }

  addPayslipFormGroup(): void {
    this.formArray.push(this.definePayslipFormGroup());
    this.updatedReasonOptionsList();
  }

  deletePayslipFormGroup(index: number): void {
    this.formArray.removeAt(index);
    this.updatedReasonOptionsList();
  }

  definePayslipFormGroup(): IPayslipFormGroup {
    const form = new FormGroup<IPayslipFormControls>({
      reason: new FormControl<SelectOption<string> | null>(null, [Validators.required]),
      paidAmount: new FormControl<string | null>(null, [Validators.required]),
    });

    this.listenReasonChanges(form);
    this.listenPaidAmountChanges(form);

    return form;
  }

  updatedReasonOptionsList(): void {
    const selectedOptionsLabels = this.formArray.controls.map((formGroup) => formGroup.controls.reason.value?.label ?? null);

    this.reasonOptionsList = this.formArray.controls.map((formGroup) => {
      if (formGroup.controls.reason.value) {
        return payslipReasonOptions
          .filter((option) => formGroup.controls.reason.value!.label == option.label || !selectedOptionsLabels.includes(option.label));
      }

      return payslipReasonOptions.filter((option) => !selectedOptionsLabels.includes(option.label));
    });
  }
}
